// styleResolver — pure functions for resolving node and connection visual styles.
// Merges topology-provided styles over built-in defaults, with unknown-type fallback.
// This is the single source of truth for how types map to visual properties.

import type { NodeTypeStyleDef, ConnectionStyleDef, LaneDef } from '../types/index.ts';

// ---------------------------------------------------------------------------
// Classification → color mapping for lanes without explicit color
// ---------------------------------------------------------------------------

const CLASSIFICATION_COLORS: Record<string, string> = {
  core: '#7B68EE',
  Core: '#7B68EE',
  supporting: '#4A90D9',
  Supporting: '#4A90D9',
  generic: '#94a3b8',
  Generic: '#94a3b8',
  external: '#e879f9',
  External: '#e879f9',
  branch: '#EF4444',
};

/** Resolve a lane's display color — uses explicit color if available, falls back to classification. */
export function resolveLaneColor(lane: LaneDef): string {
  if (lane.color) return lane.color;
  return CLASSIFICATION_COLORS[lane.classification] ?? '#94a3b8';
}

// ---------------------------------------------------------------------------
// Built-in defaults (Journey A prototype values — backward compatibility)
// ---------------------------------------------------------------------------

/** COADA NDD palette (platform NDD-PALETTE §3; Miro board legends). */
export const NDD_PALETTE = {
  moment: { fill: '#9ea9ff', role: 'Moment' },
  interface: { fill: '#f5f6f8', role: 'Interface' },
  action: { fill: '#f5d128', role: 'Action' },
  rule: { fill: '#fff9b1', role: 'Business rule / refusal' },
  process: { fill: '#c6a2d2', role: 'Process / policy / saga' },
  external: { fill: '#ffcee0', role: 'External system' },
  command: { fill: '#a6ccf5', role: 'Command' },
  event: { fill: '#ff9d48', role: 'Event' },
  projection: { fill: '#f0939d', role: 'Projection' },
  resolver: { fill: '#93d275', role: 'Resolver / query' },
  dataStore: { fill: '#67c6c0', role: 'Data store' },
  terminal: { fill: '#1a1a1a', role: 'Terminal banner' },
} as const;

export const INK_DARK = '#1a1a1a';
export const INK_LIGHT = '#ffffff';

type PaletteKey = keyof typeof NDD_PALETTE;
function pal(key: PaletteKey, icon: string, label: string): NodeTypeStyleDef {
  const p = NDD_PALETTE[key];
  return {
    icon,
    fill: p.fill,
    label,
    role: p.role,
    ink: key === 'terminal' ? INK_LIGHT : INK_DARK,
  };
}

// Mapping choices: output -> External system (it leaves the system);
// value objects / data -> Interface; branch -> Business rule / refusal.
export const DEFAULT_NODE_STYLES: Record<string, NodeTypeStyleDef> = {
  action: pal('action', '●', 'User Action'),
  command: pal('command', '▶', 'Command'),
  event: pal('event', '◆', 'Domain Event'),
  sagaEvent: pal('event', '◈', 'Saga Event'),
  coachingEvent: pal('event', '◇', 'Coaching Event'),
  policy: pal('process', '⟳', 'Policy'),
  process: pal('process', '⚙', 'Process'),
  projection: pal('projection', '▣', 'Projection'),
  saga: pal('process', '◎', 'Saga State'),
  terminal: pal('terminal', '⊘', 'Terminal'),
  branch: pal('rule', '⬦', 'Branch'),
  refusal: pal('rule', '⬦', 'Refusal'),
  output: pal('external', '☁', 'Output'),
  externalInput: pal('external', '⬡', 'External Input'),
  external: pal('external', '⬡', 'External System'),
  query: pal('resolver', '⌕', 'Query'),
  resolver: pal('resolver', '⌕', 'Resolver'),
  moment: pal('moment', '◷', 'Moment'),
  valueObject: pal('interface', '▭', 'Value Object'),
  data: pal('interface', '▭', 'Data'),
  dataStore: pal('dataStore', '▤', 'Data Store'),
};

export const DEFAULT_CONNECTION_STYLES: Record<string, ConnectionStyleDef> = {
  happy: { color: '#4ade80', dash: false, alwaysVisible: true },
  cross: { color: '#818cf8', dash: true, alwaysVisible: true },
  branch: { color: '#f59e0b', dash: true, alwaysVisible: false },
  return: { color: '#22d3ee', dash: true, alwaysVisible: true },
  'parse-fail': { color: '#ef4444', dash: true, alwaysVisible: false },
  coaching: { color: '#fb923c', dash: true, alwaysVisible: false },
  'coaching-abandon': { color: '#f87171', dash: true, alwaysVisible: false },
  disagreement: { color: '#fbbf24', dash: true, alwaysVisible: false },
};

const DEFAULT_NODE_RX: Record<string, number> = {
  branch: 10,
  terminal: 3,
};

const UNKNOWN_NODE_STYLE: NodeTypeStyleDef = {
  icon: '?',
  fill: '#888',
  label: 'Unknown',
  ink: INK_DARK,
};
const UNKNOWN_CONNECTION_STYLE: ConnectionStyleDef = { color: '#4ade8080', dash: false };

// ---------------------------------------------------------------------------
// Resolvers
// ---------------------------------------------------------------------------

export function resolveNodeStyle(
  nodeType: string,
  topologyStyles?: Record<string, NodeTypeStyleDef>,
): NodeTypeStyleDef {
  return (
    topologyStyles?.[nodeType] ??
    DEFAULT_NODE_STYLES[nodeType] ?? { ...UNKNOWN_NODE_STYLE, label: nodeType }
  );
}

export function resolveConnectionStyle(
  connStyle: string,
  topologyStyles?: Record<string, ConnectionStyleDef>,
): ConnectionStyleDef {
  return (
    topologyStyles?.[connStyle] ?? DEFAULT_CONNECTION_STYLES[connStyle] ?? UNKNOWN_CONNECTION_STYLE
  );
}

export function isConnectionAlwaysVisible(
  connStyle: string,
  topologyStyles?: Record<string, ConnectionStyleDef>,
): boolean {
  const resolved = topologyStyles?.[connStyle] ?? DEFAULT_CONNECTION_STYLES[connStyle];
  return resolved?.alwaysVisible === true;
}

export function resolveNodeRx(
  nodeType: string,
  topologyStyles?: Record<string, NodeTypeStyleDef>,
): number {
  return topologyStyles?.[nodeType]?.rx ?? DEFAULT_NODE_RX[nodeType] ?? 5;
}

/** Label ink for a tile: the style's own ink, else derived from fill luminance. */
export function resolveNodeInk(style: NodeTypeStyleDef): string {
  if (style.ink) return style.ink;
  const m = /^#([0-9a-f]{6})$/i.exec(style.fill);
  if (!m) return INK_DARK;
  const n = parseInt(m[1], 16);
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum < 0.45 ? INK_LIGHT : INK_DARK;
}
